import { isProductionHost } from "../../utils/environment";

import type { SolomonE2EChainOverride, SolomonE2EOverrides } from "./types";

/**
 * Pure (non-React) readers for the `window.__SOLOMON_E2E__` harness payload.
 *
 * Split out from the React provider so consumers that only need the raw
 * override (e.g. stores reading the chain override) don't pull the provider
 * into their dependency graph. Returns null in production / SSR — the global
 * is never set in a real session.
 */
export function readBrowserOverrides(): SolomonE2EOverrides | null {
    if (typeof window === "undefined") {
        return null;
    }

    // Defense-in-depth: never honor the E2E harness override on a production
    // host, even if `window.__SOLOMON_E2E__` were somehow injected. This is the
    // single chokepoint every override read funnels through (wallet, signer,
    // chain), so gating here makes the entire smoke-signer path unreachable in
    // production rather than relying solely on the global being absent. The
    // smoke keypair is public-by-design and grants no privilege; this just
    // removes the runtime path entirely. The build is esbuild, not Vite, so
    // there is no `import.meta.env.PROD` to fold away — the production signal
    // is the deploy hostname (`*.solomonlabs.org`).
    if (isProductionHost()) {
        return null;
    }

    return window.__SOLOMON_E2E__ ?? null;
}

export function readSolomonE2EOverrides(): SolomonE2EOverrides | null {
    return readBrowserOverrides();
}

export function readSolomonE2EChainOverride(): SolomonE2EChainOverride | null {
    return readBrowserOverrides()?.chain ?? null;
}
